import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageTitle } from "@/hooks/use-page-title";
import type { RegistryCategory, SortBy } from "../../registry";
import { useUser } from "../../utils";
import { AUDIOGAME_LIST_TITLE } from "./page-title";
import { SeasonList } from "./SeasonList";
import { SeasonListFilters } from "./SeasonListFilters";
import { SeriesFormDialog } from "./SeriesFormDialog";
import { useGenres } from "./useGenres";

export function SeasonListUI() {
  const { data: user } = useUser();
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const { query, categoryId, genreId, sortBy, page, seriesId } = useSearch({
    strict: false,
  });
  const navigate = useNavigate();

  const FILTERS_LS_PREFIX = "season-list-filters";
  const filtersLsKey = user.id ? `${FILTERS_LS_PREFIX}:${user.id}` : null;
  const restoreCheckedRef = useRef(false);

  const [restoring, setRestoring] = useState(() => {
    if (typeof window === "undefined") return false;
    if (query || categoryId || genreId || sortBy) return false;
    return true;
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once when the user id resolves
  useLayoutEffect(() => {
    if (restoreCheckedRef.current) return;
    if (!filtersLsKey) return;
    restoreCheckedRef.current = true;

    localStorage.removeItem(FILTERS_LS_PREFIX);

    if (query || categoryId || genreId || sortBy) {
      setRestoring(false);
      return;
    }

    try {
      const raw = localStorage.getItem(filtersLsKey);
      if (raw) {
        const saved = JSON.parse(raw) as Record<string, string | undefined>;
        if (saved.query || saved.categoryId || saved.genreId || saved.sortBy) {
          navigate({
            to: "/",
            search: (prev: Record<string, unknown>) => ({ ...prev, ...saved }),
            replace: true,
          });
          return;
        }
      }
    } catch {}
    setRestoring(false);
  }, [filtersLsKey]);

  useEffect(() => {
    if (
      restoring &&
      restoreCheckedRef.current &&
      (query || categoryId || genreId || sortBy)
    ) {
      setRestoring(false);
    }
  }, [restoring, query, categoryId, genreId, sortBy]);

  useEffect(() => {
    if (restoring) return;
    if (!filtersLsKey) return;
    if (!restoreCheckedRef.current) return;
    localStorage.setItem(
      filtersLsKey,
      JSON.stringify({ query, categoryId, genreId, sortBy }),
    );
  }, [restoring, query, categoryId, genreId, sortBy, filtersLsKey]);

  const setFilter = (
    updates: Partial<{
      query: string | undefined;
      categoryId: string | undefined;
      genreId: string | undefined;
      sortBy: SortBy | undefined;
      page: number | undefined;
    }>,
  ) => {
    navigate({
      to: "/",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        ...updates,
      }),
      replace: true,
    });
  };

  const setSelectedSeriesId = (id: number | undefined) => {
    navigate({
      to: "/",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        seriesId: id,
      }),
      replace: id === undefined,
    });
  };

  const { data: categories, isLoading: categoriesLoading } = useQuery<
    RegistryCategory[]
  >({
    queryKey: ["categories"],
    queryFn: () => fetch("/api/books/categories/").then((r) => r.json()),
    enabled: user.canSeeCategories,
  });

  const { data: genres } = useGenres();

  usePageTitle(AUDIOGAME_LIST_TITLE);

  if ((categoriesLoading && user.canSeeCategories) || restoring) {
    return (
      <div className="flex flex-col gap-4 mt-6">
        {/* The heading stays put while loading: the page should never be
            without one, and the skeletons carry no meaning of their own. */}
        <h1 className="font-bold text-lg leading-8 sm:truncate sm:tracking-tight">
          {AUDIOGAME_LIST_TITLE}
        </h1>
        <div role="status">
          <span className="sr-only">Loading AudioGames</span>
          <Skeleton className="h-10 w-full" aria-hidden="true" />
          <div
            className="grid grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] gap-4 mt-4"
            aria-hidden="true"
          >
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-64 w-full rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 mt-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h1 className="font-bold text-lg leading-8 sm:truncate sm:tracking-tight">
          {AUDIOGAME_LIST_TITLE}
        </h1>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-1" />
            New AudioGame
          </Button>
        </div>
      </div>

      <SeasonListFilters
        categories={categories ?? []}
        genres={genres ?? []}
        showCategories={user.canSeeCategories}
        selectedCategory={categoryId}
        selectedGenre={genreId}
        sortBy={sortBy as SortBy | undefined}
        query={query ?? ""}
        onCategoryChange={(v) => setFilter({ categoryId: v, page: undefined })}
        onGenreChange={(v) => setFilter({ genreId: v, page: undefined })}
        onSortByChange={(v) =>
          setFilter({ sortBy: v === "title" ? undefined : v, page: undefined })
        }
        onQueryChange={(v) =>
          setFilter({ query: v || undefined, page: undefined })
        }
      />

      <SeasonList
        page={page ?? 1}
        onPageChange={(p) => setFilter({ page: p === 1 ? undefined : p })}
        filters={{
          query: query || undefined,
          categoryId: categoryId ? parseInt(categoryId, 10) : undefined,
          genreId: genreId ? parseInt(genreId, 10) : undefined,
          sortBy: sortBy as SortBy | undefined,
        }}
        categories={categories ?? []}
        showCategories={user.canSeeCategories}
        onCreateNew={() => setShowCreateDialog(true)}
        selectedSeriesId={seriesId}
        onSelectedSeriesIdChange={setSelectedSeriesId}
      />

      <SeriesFormDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        categories={categories ?? []}
        showCategories={user.canSeeCategories}
      />
    </div>
  );
}
